import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { BookOpen } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { getProfession, useProfession } from "../lib/professions";

export function LibraryDialog({ open, onOpenChange, onDone }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const prof = useProfession();
  const [area, setArea] = useState(null);
  const [areas, setAreas] = useState([]);
  const activeArea = area || prof.key;

  useEffect(() => {
    if (!open) return;
    api.get("/procedures/library", { params: { area: activeArea } }).then((r) => { setItems(r.data.items); setAreas(r.data.areas); setSelected(new Set()); setFilter(""); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [open, activeArea]);

  const categories = useMemo(() => {
    const map = {};
    items.filter((i) => !filter || `${i.name} ${i.category}`.toLowerCase().includes(filter.toLowerCase()))
      .forEach((i) => { (map[i.category] ||= []).push(i); });
    return map;
  }, [items, filter]);

  const toggle = (name) => setSelected((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const toggleCategory = (list) => {
    const avail = list.filter((i) => !i.already_added).map((i) => i.name);
    const all = avail.every((n) => selected.has(n));
    setSelected((s) => { const n = new Set(s); avail.forEach((x) => (all ? n.delete(x) : n.add(x))); return n; });
  };
  const selectAll = () => setSelected(new Set(items.filter((i) => !i.already_added).map((i) => i.name)));

  const save = async () => {
    if (!selected.size) return toast.error("Selecione ao menos um procedimento");
    setSaving(true);
    try {
      const r = await api.post("/procedures/library/import", { names: [...selected], area: activeArea });
      toast.success(`${r.data.created} procedimentos adicionados! Ajuste tempos, materiais e profissional conforme sua realidade.`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl" data-testid="library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen size={20} className="text-indigo-600" /> Biblioteca de procedimentos</DialogTitle>
          <DialogDescription>Serviços com tempos médios de mercado e kit de materiais sugerido para a sua área. Selecione os que você realiza e ajuste depois.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={activeArea} onValueChange={setArea}>
            <SelectTrigger className="w-[220px]" data-testid="library-area"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{areas.map((a) => <SelectItem key={a} value={a} data-testid={`library-area-${a}`}>{getProfession(a).label}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Buscar serviço ou categoria" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="library-search" className="flex-1 min-w-[200px]" />
          <Button type="button" variant="outline" onClick={selectAll} data-testid="library-select-all">Selecionar todos</Button>
        </div>
        <div className="max-h-[50vh] overflow-auto space-y-5 pr-1" data-testid="library-list">
          {Object.entries(categories).map(([cat, list]) => (
            <div key={cat}>
              <button type="button" onClick={() => toggleCategory(list)} className="text-xs font-semibold uppercase tracking-wider text-indigo-700 hover:underline mb-2" data-testid={`library-cat-${cat}`}>{cat} · {list.length}</button>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {list.map((i) => (
                  <label key={i.name} className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${i.already_added ? "opacity-50 border-slate-100" : selected.has(i.name) ? "border-indigo-300 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
                    <Checkbox checked={i.already_added || selected.has(i.name)} disabled={i.already_added} onCheckedChange={() => toggle(i.name)} className="mt-0.5" data-testid={`library-item-${i.name}`} />
                    <span className="flex-1">
                      <span className="text-slate-800">{i.name}</span>
                      <span className="block text-xs text-slate-500">{i.duration_minutes} min{i.already_added ? " · já cadastrado" : ""}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <span className="text-sm text-slate-500 mr-auto self-center" data-testid="library-count">{selected.size} selecionado(s)</span>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" onClick={save} disabled={saving || !selected.size} className="bg-indigo-600 hover:bg-indigo-700" data-testid="library-import">{saving ? "Adicionando..." : `Adicionar ${selected.size || ""}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
